/**
 * Two vocabularies, one state machine. (doc 04 §5, doc 08 phase 4.3)
 *
 * `lib/order-lifecycle.ts` has twelve fulfilment statuses and twelve payment
 * statuses. Those are ENGINE words: `placed`, `served`, `awaiting_payment`.
 * They are precise, they are load-bearing, and no human should ever read one.
 *
 * Today they leak. A guest tracking a pickup order sees the step "Placed" —
 * which is the enum, title-cased. It tells them nothing about whether anyone
 * has looked at their food. A cook and a cashier looking at the same row need
 * different words again: `placed` is **New** at a counter and **New** on a
 * kitchen screen, but `accepted` is **Accepted** at the counter and still
 * **New** in the kitchen, because acceptance is not a cooking instruction.
 *
 * So the wording is a presentation layer, not a schema change: one function,
 * one table, keyed by `(status, surface)` and — for the two states that a
 * policy changes the meaning of — by the policy too.
 *
 * WHY POLICY IS A KEY. Under pickup approval (doc 07), `placed` no longer means
 * "the kitchen has it". It means "we have asked and nobody has answered yet",
 * and telling a guest their order was *sent* while the counter has not agreed
 * to make it is the exact false promise that flow exists to prevent. Same enum
 * value, different truth, different sentence.
 *
 * A dash in the table is deliberate: it marks a state that surface should never
 * be showing. `draft` has no guest wording because a guest cannot see a draft;
 * if one ever renders, `label()` falls back to a safe neutral phrase rather
 * than to the enum.
 */

import type { OrderStatus, PaymentStatus } from '@/lib/order-lifecycle';

/** Who is reading. Each gets its own words for the same row. */
export type StatusSurface = 'guest' | 'counter' | 'kitchen';

export type StatusVocabularyContext = {
  /**
   * Pickup approval is on for this order (doc 07 §3.1). Changes what `placed`
   * and `accepted` mean, and nothing else.
   */
  pickupApproval?: boolean;
  /**
   * How the order leaves. Only used to choose between "Ready for pickup" and a
   * bare "Ready" — a guest sitting at a table is not going anywhere to collect.
   */
  fulfilment?: 'dine_in' | 'pickup' | 'delivery' | null;
  /**
   * Seconds left on the payment window, when one is running. Rendered into the
   * guest's `accepted` line as a human countdown.
   */
  paySecondsRemaining?: number | null;
};

/**
 * `null` = this surface must not render this state. Callers treat it as "hide
 * the chip", never as "print the enum".
 */
type Row = Record<StatusSurface, string | null>;

/**
 * Where a guest's sentence will not fit — a step rail, a list chip — the short
 * form stands in. It is a shorter TRUE thing, never the enum: `placed` reads
 * "Sent", not "Placed". Absent means the long form already fits.
 */
const GUEST_SHORT: Partial<Record<OrderStatus, string>> = {
  placed: 'Sent',
  accepted: 'Accepted',
  preparing: 'Cooking',
  delivered: 'On the way',
  expired: 'Expired',
  delivery_exception: 'Held up',
};

const ORDER_WORDS: Record<OrderStatus, Row> = {
  draft: { guest: null, counter: 'Unsent', kitchen: null },
  placed: { guest: 'Sent — waiting for the kitchen', counter: 'New', kitchen: 'New' },
  // Acceptance is a promise by a person, not a cooking instruction, so the
  // kitchen still reads this ticket as New.
  accepted: { guest: 'Accepted', counter: 'Accepted', kitchen: 'New' },
  preparing: { guest: 'Being prepared', counter: 'Preparing', kitchen: 'Cooking' },
  ready: { guest: 'Ready', counter: 'Ready', kitchen: 'Done' },
  delivered: { guest: 'On the way', counter: 'Handed off', kitchen: null },
  served: { guest: null, counter: 'Served', kitchen: null },
  completed: { guest: 'Complete', counter: 'Done', kitchen: null },
  cancelled: { guest: 'Cancelled', counter: 'Cancelled', kitchen: 'Cancelled' },
  expired: { guest: 'Order expired', counter: 'Expired', kitchen: null },
  no_show: { guest: null, counter: 'No show', kitchen: null },
  // Never "delivery_exception" and never "Failed". The guest is told someone is
  // on it; the operator is told to act.
  delivery_exception: {
    guest: "We're sorting it out",
    counter: 'Needs attention',
    kitchen: null,
  },
};

const PAYMENT_WORDS: Record<PaymentStatus, Row> = {
  unpaid: { guest: 'Not paid yet', counter: 'Unpaid', kitchen: null },
  awaiting_payment: { guest: 'Waiting for your payment', counter: 'Awaiting payment', kitchen: null },
  pending_proof: { guest: 'Checking your payment', counter: 'Proof pending', kitchen: null },
  pending_review: { guest: 'Checking your payment', counter: 'Needs review', kitchen: null },
  partial: { guest: 'Part paid', counter: 'Part paid', kitchen: null },
  paid: { guest: 'Paid', counter: 'Paid', kitchen: null },
  partially_refunded: { guest: 'Partly refunded', counter: 'Partly refunded', kitchen: null },
  refunded: { guest: 'Refunded', counter: 'Refunded', kitchen: null },
  void: { guest: 'Cancelled', counter: 'Void', kitchen: null },
  invoiced: { guest: 'On your invoice', counter: 'Invoiced', kitchen: null },
  waived: { guest: 'Nothing to pay', counter: 'Waived', kitchen: null },
  comped: { guest: 'On the house', counter: 'Comped', kitchen: null },
};

/** Anything rather than an enum. Used when a surface asks for a state it should not see. */
const NEUTRAL: Record<StatusSurface, string> = {
  guest: 'In progress',
  counter: 'In progress',
  kitchen: 'In progress',
};

function countdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * The guest's word for a fulfilment status.
 *
 * Returns `null` where the guest should see nothing at all, so a caller can
 * hide the chip instead of inventing a sentence.
 */
export function orderStatusWord(
  status: OrderStatus | string | null | undefined,
  surface: StatusSurface,
  context: StatusVocabularyContext = {},
): string | null {
  if (!status) return null;
  const row = ORDER_WORDS[status as OrderStatus];
  if (!row) return NEUTRAL[surface];

  if (surface === 'guest') {
    // ── The two states pickup approval re-defines ──
    if (context.pickupApproval && status === 'placed') {
      return 'Waiting for them to confirm';
    }
    if (context.pickupApproval && status === 'accepted') {
      const left = context.paySecondsRemaining;
      return typeof left === 'number' ? `Confirmed — pay within ${countdown(left)}` : 'Confirmed';
    }
    if (status === 'ready' && context.fulfilment === 'pickup') return 'Ready for pickup';
  }

  return row[surface];
}

/**
 * The guest's word, shortened to fit a chip or a step rail.
 *
 * The long form is the one to reach for; this exists because a four-column rail
 * cannot carry "Sent — waiting for the kitchen" without truncating it to
 * something that reads as an error.
 */
export function orderStatusChip(
  status: OrderStatus | string | null | undefined,
  context: StatusVocabularyContext = {},
): string | null {
  if (!status) return null;
  if (context.pickupApproval && status === 'placed') return 'Waiting';
  if (context.pickupApproval && status === 'accepted') return 'Confirmed';
  const short = GUEST_SHORT[status as OrderStatus];
  if (short) return short;
  return orderStatusWord(status, 'guest', context);
}

/** The same, for payment state. */
export function paymentStatusWord(
  status: PaymentStatus | string | null | undefined,
  surface: StatusSurface,
): string | null {
  if (!status) return null;
  const row = PAYMENT_WORDS[status as PaymentStatus];
  if (!row) return NEUTRAL[surface];
  return row[surface];
}

/**
 * Never-null variant, for a chip that must render something. Prefer
 * `orderStatusWord` and hide the chip on `null`; reach for this only where the
 * layout has a hole without a label.
 */
export function orderStatusLabel(
  status: OrderStatus | string | null | undefined,
  surface: StatusSurface,
  context: StatusVocabularyContext = {},
): string {
  return orderStatusWord(status, surface, context) ?? NEUTRAL[surface];
}
